// Primitivas de solo lectura para el informe de respuestas de actividades.
// Las "preguntas" se derivan de activity_templates y las respuestas de
// student_progress.response (TEXT: JSON serializado o string plano).

const EXCLUDED_INPUT_TYPES = ["skool_invite"];
const SINGLE_QUESTION_INPUT_TYPES = ["text", "yesno"];

// heading es decorativo; file se deja para una fase posterior (Storage).
const NON_REPORTABLE_FIELD_TYPES = ["heading", "file"];

// Campos de config.fields que sí pueden aparecer en el informe.
export function getReportableFields(template) {
  const fields = template?.config?.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter(
    (field) => field && !NON_REPORTABLE_FIELD_TYPES.includes(field.type)
  );
}

// Preguntas seleccionables de una plantilla, en el orden de config.fields.
// La clasificación se hace por config.fields, NO por input_type: hay
// actividades con input_type 'checkbox' que en realidad son formularios.
export function getSelectableQuestions(template) {
  if (!template || EXCLUDED_INPUT_TYPES.includes(template.input_type)) {
    return [];
  }

  const common = {
    templateId: template.id,
    templateName: template.name || "Actividad sin nombre",
    blockName: template.block_name || "Sin bloque",
    isArchived: template.is_active === false,
    adminOnly: template.admin_only === true,
  };

  const fields = getReportableFields(template);
  if (fields.length > 0) {
    return fields.map((field, index) => ({
      ...common,
      questionId: `${template.id}:${field.id ?? index}`,
      label: (field.label || "").trim() || `Campo ${index + 1}`,
      fieldId: field.id ?? null,
      kind: "form",
    }));
  }

  if (SINGLE_QUESTION_INPUT_TYPES.includes(template.input_type)) {
    return [
      {
        ...common,
        questionId: `${template.id}:_single`,
        label: common.templateName,
        fieldId: null,
        kind: "single",
      },
    ];
  }

  // checkbox sin config, o formulario que solo tenía campos file.
  return [];
}

// Devuelve un objeto (formulario) o un string plano (text/yesno).
// Nunca lanza: una fila corrupta no puede tumbar el informe.
export function parseResponse(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return String(raw);

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch {
    // No es JSON: es una respuesta de texto plano.
  }
  return raw;
}

// Valor de una pregunta como TEXTO tal cual (sin convertir números).
export function getAnswer(template, question, parsedResponse) {
  if (parsedResponse === null || parsedResponse === undefined) return "";

  if (question.kind === "form") {
    if (typeof parsedResponse !== "object" || !question.fieldId) return "";
    const value = parsedResponse[question.fieldId];
    if (value === null || value === undefined) return "";
    return typeof value === "string" ? value : String(value);
  }

  return typeof parsedResponse === "string" ? parsedResponse : "";
}

// ¿La actividad tiene contenido aprovechable para el informe?
// "No" o "Pendiente" son respuestas válidas, no ausencia de respuesta.
export function hasRealAnswer(template, parsedResponse) {
  if (parsedResponse === null || parsedResponse === undefined) return false;

  const fields = getReportableFields(template);
  if (fields.length > 0) {
    if (typeof parsedResponse !== "object") return false;
    return fields.some((field) => {
      const value = parsedResponse[field.id];
      if (value === null || value === undefined) return false;
      return String(value).trim() !== "";
    });
  }

  if (SINGLE_QUESTION_INPUT_TYPES.includes(template?.input_type)) {
    return (
      typeof parsedResponse === "string" && parsedResponse.trim() !== ""
    );
  }

  return false;
}
